import React, { useEffect, useState } from "react";
import { Activity, Cpu, Globe, Radar, Sparkles, Zap } from "lucide-react";

const TICKER_ITEMS = [
  { l: "FIDIC YELLOW", v: "Sub-Cl. 20.2.1 · 28-day", c: "text-amber-400" },
  { l: "ACTIVE CLAIMS", v: "6", c: "text-cyan-400" },
  { l: "EOT POOLED", v: "147 days", c: "text-cyan-400" },
  { l: "COST AT RISK", v: "$ 12.4 mn", c: "text-rose-400" },
  { l: "SAVINGS IDEAS", v: "9 open", c: "text-emerald-400" },
  { l: "FORECAST EAC", v: "$ 658 mn", c: "text-cyan-400" },
  { l: "NEXT MILESTONE", v: "Block-04 fdn · 11d", c: "text-amber-400" },
  { l: "UPTIME", v: "99.97%", c: "text-emerald-400" },
];

const GLITTER_COUNT = 22;

export default function HeroBanner() {
  const [time, setTime] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // pre-compute glitter positions once
  const glitter = Array.from({ length: GLITTER_COUNT }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 6,
    duration: 4 + Math.random() * 4,
    size: 1 + Math.random() * 2,
  }));

  return (
    <div
      className="relative overflow-hidden border border-slate-800 rounded-sm bg-slate-950"
      data-testid="hero-banner"
    >
      {/* animated grid background */}
      <div className="absolute inset-0 hero-grid" />

      {/* floating gradient orbs */}
      <div
        className="hero-orb"
        style={{
          left: "-80px",
          top: "-80px",
          width: 320,
          height: 320,
          background: "radial-gradient(circle, #06b6d4 0%, transparent 70%)",
        }}
      />
      <div
        className="hero-orb"
        style={{
          right: "-100px",
          bottom: "-120px",
          width: 380,
          height: 380,
          background: "radial-gradient(circle, #1d4ed8 0%, transparent 70%)",
          animationDelay: "3s",
        }}
      />

      {/* glitter particles */}
      <div className="glitter">
        {glitter.map((g, i) => (
          <span
            key={i}
            style={{
              left: `${g.left}%`,
              bottom: 0,
              width: g.size,
              height: g.size,
              animationDelay: `${g.delay}s`,
              animationDuration: `${g.duration}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 px-6 sm:px-10 py-10 sm:py-14 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
        {/* Left – headline */}
        <div className="lg:col-span-7 fade-up">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 border border-cyan-500/30 px-2.5 py-1 rounded-sm bg-cyan-500/5">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
              Live · Engineering Intelligence
            </span>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
              <Cpu className="h-3 w-3" />
              Claude Sonnet 4.5
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-[58px] font-bold leading-[1.05] tracking-tight">
            <span className="block text-slate-200">Engineering decisions,</span>
            <span className="block shimmer">in seconds — not weeks.</span>
          </h1>

          <p className="text-sm sm:text-base text-slate-400 mt-5 max-w-xl leading-relaxed">
            FIDIC contract intelligence · project schedule risk · financial
            MIS · automated notices &amp; claims — one decision-grade platform
            for global EPC professionals.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Pill icon={Sparkles} label="6 AI Modules" />
            <Pill icon={Globe} label="FIDIC Red · Yellow · Silver · Green" />
            <Pill icon={Radar} label="Real-time alerts" />
            <Pill icon={Zap} label="Notice in &lt; 30 sec" />
          </div>
        </div>

        {/* Right – 3D rotating sphere + clock */}
        <div className="lg:col-span-5 flex flex-col items-center gap-6">
          <div className="relative w-[260px] h-[260px] flex items-center justify-center">
            <div className="absolute inset-2 radar opacity-60" />
            <div className="sphere">
              <div className="ring" />
              <div className="ring" />
              <div className="ring" />
              <div className="ring" />
              <div className="ring" />
              <div className="core" />
            </div>
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400/70">
              ◢ NEURAL CORE ◣
            </div>
          </div>

          <div className="flex items-center gap-6 text-xs font-mono">
            <Stat label="UTC" value={fmtTime(time)} />
            <Stat label="DATE" value={fmtDate(time)} />
            <Stat label="STATUS" value="ONLINE" green />
          </div>
        </div>
      </div>

      {/* live ticker */}
      <div className="relative z-10 border-t border-slate-800 bg-slate-950/80 backdrop-blur">
        <div className="flex items-center gap-3 px-4 py-2 overflow-hidden">
          <div className="shrink-0 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400 pr-3 border-r border-slate-800">
            <Activity className="h-3 w-3 animate-pulse" />
            Live Stream
          </div>
          <div className="overflow-hidden flex-1">
            <div className="ticker-track">
              {[...TICKER_ITEMS, ...TICKER_ITEMS].map((it, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 text-xs font-mono shrink-0"
                >
                  <span className="text-slate-500">{it.l}</span>
                  <span className={it.c}>{it.v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Pill({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-sm border border-slate-800 bg-slate-900/70 text-slate-300 hover:border-cyan-500/50 hover:text-cyan-300 transition-colors">
      <Icon className="h-3 w-3 text-cyan-400" />
      <span dangerouslySetInnerHTML={{ __html: label }} />
    </span>
  );
}

function Stat({ label, value, green }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
        {label}
      </span>
      <span
        className={`mt-0.5 ${green ? "text-emerald-400" : "text-slate-100"}`}
      >
        {green ? <span className="inline-flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
          {value}
        </span> : value}
      </span>
    </div>
  );
}

function fmtTime(d) {
  return d.toISOString().slice(11, 19);
}
function fmtDate(d) {
  return d.toISOString().slice(0, 10);
}
